"use client";

import TailwindAdvancedEditor from "@/components/tailwind/advanced-editor";
import Menu from "@/components/tailwind/ui/menu";
import { FileText, X, Lightbulb, ArrowLeft } from "lucide-react";
import Link from "next/link";
import { useState, useEffect } from "react";
import { OrchantaMark } from "@/components/orchanta-mark";

function TipBar() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem("editor:tips-dismissed")) setVisible(true);
  }, []);
  if (!visible) return null;
  const dismiss = () => { localStorage.setItem("editor:tips-dismissed", "1"); setVisible(false); };

  return (
    <div className="w-full max-w-screen-lg mx-auto px-4 animate-fade-up" style={{ animationDelay: "0.2s", opacity: 0 }}>
      <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/50 text-xs text-blue-700 dark:text-blue-300">
        <Lightbulb className="w-3.5 h-3.5 shrink-0" />
        <span className="flex-1">
          Type <kbd className="px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/50 font-mono text-[10px]">/</kbd> for commands
          <span className="mx-2 text-blue-300 dark:text-blue-700">·</span>
          Select text → <span className="font-medium">Ask AI</span>
          <span className="mx-2 text-blue-300 dark:text-blue-700">·</span>
          Type <kbd className="px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/50 font-mono text-[10px]">++</kbd> for autocomplete
        </span>
        <button onClick={dismiss} className="p-0.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function EditorPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-violet-50/20 dark:from-[#0a0a14] dark:via-[#0a0a14] dark:to-[#0d0a1a] relative overflow-hidden">
      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-blue-200/20 dark:bg-blue-600/5 blur-3xl" />
        <div className="absolute top-1/2 -left-32 w-96 h-96 rounded-full bg-violet-200/15 dark:bg-violet-600/5 blur-3xl" />
      </div>

      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/70 dark:bg-[#0a0a14]/70 border-b border-black/5 dark:border-white/5">
        <div className="max-w-screen-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link href="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-xs hidden sm:inline">Back</span>
            </Link>
            <div className="w-px h-5 bg-border" />
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center">
                <FileText className="w-3 h-3 text-white" />
              </div>
              <span className="text-sm font-semibold text-foreground">Quill</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link href="/" aria-label="Quill — home" className="hidden sm:inline-flex">
              <OrchantaMark theme="light" />
            </Link>
            <Menu />
          </div>
        </div>
      </nav>

      <div className="relative flex flex-col items-center gap-4 pt-4 pb-4 sm:px-5" style={{ zIndex: 1 }}>
        <TipBar />
        <div className="animate-fade-up w-full flex justify-center" style={{ animationDelay: "0.1s", opacity: 0 }}>
          <TailwindAdvancedEditor />
        </div>
      </div>
    </div>
  );
}
